import { Box, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'commons/store/hooks';
import Dependency from 'core/helpers/Dependency';
import QueriesPanel from 'features/survey/components/QueriesPanel';
import {
  fetchSurvey,
  fetchSurveyElements,
  selectCurrentSection,
  selectDependencies,
  selectSurvey,
  selectSurveyElements
} from 'features/survey/redux/surveySlice';
import { selectElements } from 'features/survey/redux/sectionSlice';
import getInitialValues from 'features/survey/utils/getInitialValues';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StyledSurveyForm, StyledSurveyTitle } from '../Form.styled';
import FormField from '../FormField';
import { Divider } from './Divider';
import { useSnackbar } from 'notistack';
import axiosSecureInstance from 'commons/axios/axiosSecureInstance';
import { flatten } from 'lodash';
import ModalForm from "components/Form/Modal/ModalForm";
import FormUnsavedChangesGuardDialog from "../FormUnsavedChangesGuardDialog";
import CenteredContent from "components/CenteredContent";
import { useTranslation } from "react-i18next";
import FormikEffect from "../FormikEffect";
import { FORM_AUTOSAVE } from "commons/constants/env";
import { selectUserProfile } from "core/redux/userSlice";
import {roles} from "../../../../../commons/auth/roles";

interface Props {
  isDataLoading: boolean;
  setIsDataLoading: (isDataLoading: boolean) => void;
  isOpenQueryPanel: null | string;
  setIsOpenQueryPanel: (isOpenQueryPanel: null | string) => void;
  setSelectedSegment: (selectedSegment: any) => void;
  updateSection: (sectionId: string, segments: any) => void;
}

export const FormContent: React.FC<Props> = ({
    isDataLoading,
    setIsDataLoading,
    isOpenQueryPanel,
    setIsOpenQueryPanel,
    updateSection
}) => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    /** This is current selected section data */
    const currentSection = useAppSelector(selectCurrentSection);

    /** ECRF preps - holding data of actual Survey */
    const survey = useAppSelector(selectSurvey);
    const surveyElementData = useAppSelector(selectSurveyElements);
    const sectionElements = useAppSelector(selectElements);
    const dependencies = useAppSelector(selectDependencies);
    const userProfile = useAppSelector(selectUserProfile);

    /** Parameters */
    const { id, section } = useParams<{ id: string, section: string }>();
    const [dataLoading, setDataLoading] = useState(t('Loading data') + '...')
    const [sectionDependencies, setSectionDependencies] = useState<any>([]);
    const [hasElementDataInstance, setHasElementDataInstance] = useState(false);

    /**
     * Check Survey state
     */
    const checkState = async () => {
        switch (survey?.state) {
            case 1:
                setIsDataLoading(true);
                setDataLoading(t('Initializing data') + '...');
                break;
            case 2:
                setIsDataLoading(true);
                setDataLoading(t('PLEASE WAIT, LOADING NEW PATIENT'));
                break;
            case 3:
                setIsDataLoading(true);
                setDataLoading(t('Patient randomization is in progress') + '...');
                break;
            default:
                setIsDataLoading(false);
                setDataLoading(t('Loading data') + '...');
        }
    }

    /**
     * Load section data elements
     * @param section
     * @param data
     */
    const loadData = async (section: any, data: any = []) => {
        try {
            if (section.id) {
                // elements
                // const els = await dispatch(fetchElements(section.id)).unwrap();

                // dependencies
                const depends = await Dependency.createDep(
                    // section?.elements, elements, id, section?.dependencies
                    // section?.elements, elements, id, dependencies
                  sectionElements, data, id, dependencies
                );

                if (depends) {
                    setSectionDependencies(depends);
                }
            }
        } catch (error: any) {
            enqueueSnackbar(
                'Failed to load section, refresh the page or contact the administrator.',
                { variant: 'error' }
            );
        }

        switch (survey?.state) {
            case 1:
            case 2:
            case 3:
                setTimeout(async () => {
                    id && await dispatch(fetchSurvey(id));
                }, 6000);
                break;
            default:
                setIsDataLoading(false);
                setDataLoading(t('Loading data') + '...');
        }
    }

    /**
     * This function will save all completed fields after clicking SAVE ALL
     * @param values
     */
    const handleSubmit = async (values: any) => {
        setIsAllSaving(true);

        const parsedValues = Object.entries(values);

        // map form values and create data object
        let data = [];
        for (const element of parsedValues) {
            const elementId: string = element[0];
            const elementData: any  = element[1];

            if (surveyElementData.find(
                (e: any) => e?.element?.id === elementId && e?.data[0] === elementData
            )) {
                continue;
            }

            if (
                elementData !== '' &&
                elementData !== null &&
                elementData !== undefined &&
                elementData.length !== 0
            ) {
                const item = {
                    data: flatten([elementData]),
                    element: `/api/elements/${elementId}`
                }

                data.push(item);
            }
        }

        if (data.length > 0) {
            await saveData(data).then();
        }

        setIsAllSaving(false);
    };

    /** Request saving the current section */
    const [isAllSaving, setIsAllSaving] = useState(false);
    const saveData = async (elements: any) => {
        try {
            // await axiosSecureInstance.put(`/api/ecrves/${id}/${currentSection.id}/elements`, {elements});
            await axiosSecureInstance.put(`/api/ecrves/${id}/elements`, { elements });
            await dispatch(fetchSurveyElements({ id, sectionId: section }));

            enqueueSnackbar(`Saved ${new Date().toLocaleTimeString()}`, { variant: 'info' });
        } catch (error: any) {
            enqueueSnackbar(error?.detail || error?.response?.data?.detail, {
                variant: 'error',
            });
        }
    };

    useEffect(() => {
        // if (currentSection?.elements && currentSection?.dependencies && elements) {
        // if (currentSection?.elements && dependencies && elements) {
        if (currentSection && sectionElements && dependencies && surveyElementData) {
            loadData(currentSection, surveyElementData).then();
        }
    }, [survey, surveyElementData]);

    useEffect(() => {
        checkState()
    }, [survey, currentSection, surveyElementData]);

    useEffect(() => {
        if (isDataLoading) {
            setIsDataLoading(true);
            setSectionDependencies([]);
        }
    }, [currentSection]);

    // @todo rebuild this, optimize
    const initialValues = getInitialValues(sectionElements || [], surveyElementData || []);

    const validateElements = async (elements: any, values: any) => {
      const errors: any = {};
      const defaultError = "To pole jest wymagane.";

      elements.filter((el: any) => el.required).forEach((element: any) => {
        if (values[element.name] == undefined) {
          errors[element.name] = defaultError;
        } else {
          switch (element.type) {
            case 'association':
              validateElements(element.children, values);
              break;
            case 'collection':
              break;
            case 'number':
            case 'text':
              if (values[element.name] == null || values[element.name].length == 0 || values[element.name].trim().length == 0) {
                errors[element.name] = defaultError;
              }
              break;
            default:
              if (values[element.name] == null) {
                errors[element.name] = defaultError;
              }
              break;
          }
        }
      });

      return errors;
    }

    const validate = async (values: any) => {
      // const formInputs = await dependency(formData?.elements, values) || [];
      return validateElements(sectionElements?.filter((el: any) => el.required), values);
    };

    return (
        <>
            <StyledSurveyForm>
                {isDataLoading ? (
                    <CenteredContent>
                      <CircularProgress />
                      <StyledSurveyTitle>{dataLoading}</StyledSurveyTitle>
                    </CenteredContent>
                ) : (
                    <Formik
                      initialValues={initialValues}
                      enableReinitialize={true}
                      validate={validate}
                      onSubmit={handleSubmit}
                    >
                      <Form>
                        {/* Guard auto-detects dirty state from Formik */}
                        <FormUnsavedChangesGuardDialog />

                        {(
                          FORM_AUTOSAVE &&
                          // 4 test
                          userProfile.roles.some((role: string) => roles.admin.includes(role)) &&
                          id && section
                        ) && <FormikEffect surveyId={id} sectionId={section}/>}

                        {sectionElements.length === 0 &&
                          <CenteredContent>{t('No items')}</CenteredContent>
                        }

                          {sectionDependencies && sectionDependencies.length > 0 && (
                              <Box>
                                  {sectionDependencies.slice().map((element: any) => {
                                      /** Divider is Header type */
                                      if (element.type === 'divider') {
                                          return <Divider key={element?.id} label={element.label}/>;
                                      }

                                      /** Find data of element and interpose it */
                                      /** @todo data assign in dependencies builder */
                                      const data = surveyElementData.findLast(
                                          (data: any) => data?.element?.id === element.id
                                      );

                                      /** Render element - check type and display it */
                                      return (
                                          <FormField
                                              element={element}
                                              key={element?.id}
                                              surveyId={survey?.id}
                                              elementStatus={data?.status}
                                              elementData={data}
                                              hasActionPanel={element?.helper === 0}
                                              hasElementDataInstance={hasElementDataInstance}
                                              hasQueryPanel={element?.helper === 0}
                                              isOpenQueryPanel={isOpenQueryPanel}
                                              setIsOpenQueryPanel={(elementId) => setIsOpenQueryPanel(elementId)}
                                              isAllSaving={isAllSaving}
                                              setIsAllSaving={setIsAllSaving}
                                              updateSection={updateSection}
                                          />
                                      );
                                  })}
                              </Box>
                          )}

                          {/** Navigation widget */}
                          {/*<NavWidget*/}
                          {/*    isAllSaving={isAllSaving}*/}
                          {/*    setIsAllSaving={setIsAllSaving}*/}
                          {/*    updateSection={updateSection}*/}
                          {/*/>*/}
                        </Form>
                    </Formik>
                )}
            </StyledSurveyForm>

            {/** Modal form */}
            <ModalForm />

            {/** QueriesPanel window */}
            <QueriesPanel
                currentSurveyId={id || ''}
                isOpen={!!isOpenQueryPanel}
                queryId={
                    surveyElementData?.find((data: any) => data?.element?.id === isOpenQueryPanel)?.comment?.id || null
                }
                onClose={() => setIsOpenQueryPanel(null)}
                elementDataId={
                    // TODO swagger model for element data
                    surveyElementData?.find((data: any) => data?.element?.id === isOpenQueryPanel)?.id ||
                    null
                }
                element={
                    isOpenQueryPanel
                        ? sectionElements?.find((element: any) => element.id === isOpenQueryPanel)
                        : undefined
                }
            />
        </>
    );
};
